// Segunda fatura legítima da mesma UC no mesmo mês.
//
// O banco recusa duplicata por padrão (gatilho `prevent_duplicate_active_fatura`,
// migration 20260922160000) — é o que protege a base das duplicatas que o robô cria
// sem querer. A exceção é explícita: alguém do Financeiro registra uma AUTORIZAÇÃO
// com motivo, e ela libera uma única inserção daquela UC + mês.
//
// Aqui fica só a parte pura (sem Supabase), para rodar em `node --test`:
//   node --experimental-strip-types --test src/lib/__tests__/duplicataFatura.test.ts

/** Mínimo do motivo — mesmo do CHECK da tabela `faturas_duplicata_autorizada`. */
export const MOTIVO_MINIMO = 10;

/** Texto que o gatilho usa na exceção; serve para reconhecer o erro venha de onde vier. */
const MARCA_ERRO = 'duplicata bloqueada';

/**
 * O erro que voltou é a recusa de duplicata?
 *
 * Cobre o erro direto do PostgREST (código 23505 + mensagem do gatilho) e o texto
 * repassado pelo n8n no upload de PDF, que chega só como string.
 */
export function ehErroDuplicataFatura(erro: unknown): boolean {
  if (!erro) return false;
  const texto = typeof erro === 'string'
    ? erro
    : [(erro as any)?.message, (erro as any)?.error, (erro as any)?.details, (erro as any)?.hint]
        .filter(Boolean)
        .join(' ');
  if (texto.toLowerCase().includes(MARCA_ERRO)) return true;
  // 23505 sozinho não basta: qualquer chave única do banco usa esse código, e o texto
  // em inglês ("duplicate key value…") não é o nosso caso — por isso só "duplicata"/"fatura".
  return String((erro as any)?.code || '') === '23505' && /duplicata|fatura/i.test(texto);
}

/**
 * Tira a UC e o mês da mensagem do gatilho ("…para UC 348368701220 em SET/2026.").
 * Devolve `null` quando a mensagem não tem esse formato — aí a tela pergunta.
 */
export function extrairUcEMesDoErro(erro: unknown): { uc: string; mes: string } | null {
  const texto = typeof erro === 'string' ? erro : String((erro as any)?.message || (erro as any)?.error || '');
  const m = texto.match(/UC\s+([\w.\-]+)\s+em\s+([A-Za-zÇç]{3}\/\d{4})/i);
  if (!m) return null;
  return { uc: m[1], mes: m[2].toUpperCase() };
}

/**
 * Mesma normalização da coluna gerada `faturas.uc_codigo_norm`: sem ponto, traço,
 * espaço e sem zero à esquerda. Usada só para comparar UCs na tela.
 */
export function normalizarUcFatura(uc: unknown): string {
  return String(uc ?? '').replace(/[.\-\s]/g, '').replace(/^0+/, '');
}

/** Status em que a fatura não concorre por duplicata — os mesmos do gatilho do banco. */
export const STATUS_FORA_DA_DUPLICATA = ['Excluído', 'Cancelada', 'Aguardando Upload'];

/**
 * Esta fatura entra na conta de duplicata?
 *
 * Mesma regra do gatilho `prevent_duplicate_active_fatura`: fatura AVULSO nunca é
 * duplicata (é cobrança à parte, como um acordo, e pode conviver com a fatura do mês),
 * e fatura excluída, cancelada ou só reservada ("Aguardando Upload") também não.
 *
 * A tela contava todas elas: em 23/09/2026 isso marcava 521 grupos de UC+mês+vencimento
 * como duplicata, sendo 9 duplicatas de verdade.
 */
export function faturaEntraNaDuplicata(fatura: { classificacao?: string | null; status_fatura?: string | null } | null | undefined): boolean {
  if (!fatura) return false;
  if ((fatura.classificacao || '').trim().toUpperCase() === 'AVULSO') return false;
  return !STATUS_FORA_DA_DUPLICATA.includes((fatura.status_fatura || '').trim());
}

export type FaturaMarcavel = {
  classificacao?: string | null;
  status_fatura?: string | null;
  duplicata_confirmada?: boolean | null;
};

/**
 * Marca cada fatura como duplicata ou não. `chave` é quem diz o que é "a mesma
 * fatura" (na tela: UC + mês + vencimento, cada um normalizado do jeito de lá).
 *
 * Só contam — e só são marcadas — as faturas que entram na duplicata; e quem já foi
 * confirmada como legítima não volta a acender o alerta.
 */
export function marcarDuplicatas<T extends FaturaMarcavel>(faturas: T[], chave: (f: T) => string): (T & { isDuplicated: boolean })[] {
  const contagem = new Map<string, number>();
  for (const f of faturas) {
    if (!faturaEntraNaDuplicata(f)) continue;
    const k = chave(f);
    contagem.set(k, (contagem.get(k) || 0) + 1);
  }
  return faturas.map(f => ({
    ...f,
    isDuplicated: faturaEntraNaDuplicata(f) && (contagem.get(chave(f)) || 0) > 1 && !f.duplicata_confirmada,
  }));
}

export function motivoDuplicataValido(motivo: string): boolean {
  return motivo.trim().length >= MOTIVO_MINIMO;
}

/** Mês de referência aceito pelo sistema: "SET/2026". */
export function mesReferenciaValido(mes: string): boolean {
  return /^(JAN|FEV|MAR|ABR|MAI|JUN|JUL|AGO|SET|OUT|NOV|DEZ)\/\d{4}$/.test(mes.trim().toUpperCase());
}
